import { useMemo } from "react";

import { Link, useSearchParams } from "react-router-dom";

import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ColumnDef, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { BellRing, Globe, Inbox, RotateCw, Send } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { adminApi } from "@/api/endpoints";
import {
  ActiveFilters,
  ColumnVisibility,
  DataTable,
  TablePagination,
} from "@/components/shared/data-table";
import { DeliveryStatusBadge } from "@/components/shared/delivery-status-badge";
import { EmptyState } from "@/components/shared/empty-state";
import { PageHeader } from "@/components/shared/page-header";
import { RememberParams } from "@/components/shared/remember-params";
import { Button } from "@/components/ui/button";
import { currentLanguage } from "@/configs/i18n";
import { queryKeys } from "@/configs/query-keys";
import { useColumnVisibility } from "@/hooks/use-column-visibility";
import { useErrorToast } from "@/hooks/use-error-toast";
import { type FilterBinding, useUrlColumnFilters } from "@/hooks/use-url-column-filters";
import { useUrlPagination } from "@/hooks/use-url-pagination";
import { formatDateTime } from "@/lib/format";
import { readEnum } from "@/lib/url-params";
import { cn } from "@/lib/utils";
import { type AdminDelivery, DELIVERY_CHANNELS, DELIVERY_STATUSES } from "@/types/api";

const BINDINGS: FilterBinding[] = [
  { columnId: "channel", param: "channel", allowed: DELIVERY_CHANNELS },
  { columnId: "status", param: "status", allowed: DELIVERY_STATUSES },
];

const REMEMBERED = ["status", "channel", "limit"] as const;

export function DeliveriesPage() {
  const { t } = useTranslation();
  return (
    <>
      <PageHeader
        title={t("deliveries.title")}
        subtitle={t("deliveries.subtitle")}
        icon={BellRing}
        tone="amber"
      />
      <RememberParams table="deliveries" keys={REMEMBERED}>
        <DeliveriesTable />
      </RememberParams>
    </>
  );
}

function DeliveriesTable() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const queryClient = useQueryClient();
  const showError = useErrorToast();
  const { page, limit, setPage, setLimit } = useUrlPagination();
  const { columnFilters, onColumnFiltersChange } = useUrlColumnFilters(BINDINGS);
  const { columnVisibility, onColumnVisibilityChange } = useColumnVisibility("deliveries", {
    sentAt: false,
  });

  const filters = {
    page,
    limit,
    status: readEnum(params, "status", DELIVERY_STATUSES),
    channel: readEnum(params, "channel", DELIVERY_CHANNELS),
  };
  const deliveries = useQuery({
    queryKey: queryKeys.deliveries(filters),
    queryFn: () => adminApi.deliveries(filters),
    placeholderData: keepPreviousData,
  });

  const retry = useMutation({
    mutationFn: adminApi.retryDelivery,
    onSuccess: result => {
      toast.success(t("deliveries.retried", { status: t(`deliveries.statuses.${result.status}`) }));
      void queryClient.invalidateQueries({ queryKey: queryKeys.deliveriesAll });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboardAll });
    },
    onError: showError,
  });

  const columns = useDeliveryColumns(retry.mutate, retry.isPending ? retry.variables : undefined);
  const table = useReactTable({
    data: deliveries.data?.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: row => row.id,
    state: {
      columnFilters,
      columnVisibility,
      pagination: { pageIndex: page - 1, pageSize: limit },
    },
    onColumnFiltersChange,
    onColumnVisibilityChange,
    manualFiltering: true,
    manualPagination: true,
    enableSorting: false,
    pageCount: deliveries.data?.meta.totalPages ?? 0,
  });

  return (
    <section className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <ActiveFilters table={table} />
        <div className="ml-auto">
          <ColumnVisibility table={table} />
        </div>
      </div>
      <DataTable
        table={table}
        isLoading={deliveries.isPending}
        isFetching={deliveries.isFetching}
        error={deliveries.error}
        onRetry={() => void deliveries.refetch()}
        rowClassName={row =>
          row.original.status === "FAILED" ? "bg-destructive/[0.03]" : undefined
        }
        empty={
          <EmptyState
            icon={Inbox}
            title={t("deliveries.emptyTitle")}
            text={t("deliveries.emptyText")}
          />
        }
      />
      {deliveries.data && deliveries.data.meta.total > 0 ? (
        <TablePagination
          page={page}
          limit={limit}
          total={deliveries.data.meta.total}
          totalPages={deliveries.data.meta.totalPages}
          onPage={setPage}
          onLimit={setLimit}
        />
      ) : null}
    </section>
  );
}

function useDeliveryColumns(
  onRetry: (id: string) => void,
  retryingId: string | undefined,
): ColumnDef<AdminDelivery>[] {
  const { t } = useTranslation();
  const lang = currentLanguage();

  return useMemo<ColumnDef<AdminDelivery>[]>(
    () => [
      {
        id: "type",
        accessorKey: "notificationType",
        header: t("deliveries.type"),
        enableHiding: false,
        meta: { label: t("deliveries.type"), width: 320 },
        cell: ({ row }) => {
          const delivery = row.original;
          return (
            <div className="min-w-0 space-y-0.5">
              <p className="truncate font-medium">
                {t(`deliveries.types.${delivery.notificationType}`)}
              </p>
              <p className="text-muted-foreground truncate text-xs">{delivery.title}</p>
              {delivery.error ? (
                <p className="text-destructive bg-destructive/5 mt-1 rounded-md px-2 py-1 font-mono text-[11px] break-words whitespace-normal">
                  {delivery.error}
                </p>
              ) : null}
            </div>
          );
        },
      },
      {
        id: "recipient",
        accessorKey: "userName",
        header: t("deliveries.recipient"),
        meta: { label: t("deliveries.recipient"), width: 180 },
        cell: ({ row }) => (
          <Link
            to={`/users/${row.original.userId}`}
            className="hover:text-primary font-medium underline-offset-4 hover:underline"
          >
            {row.original.userName ?? row.original.telegramUserId}
          </Link>
        ),
      },
      {
        id: "channel",
        accessorKey: "channel",
        header: t("deliveries.channel"),
        meta: {
          label: t("deliveries.channel"),
          width: 140,
          filterVariant: "select",
          filterOptions: DELIVERY_CHANNELS.map(channel => ({
            value: channel,
            label: t(`deliveries.channels.${channel}`),
          })),
        },
        cell: ({ row }) => {
          const telegram = row.original.channel === "TELEGRAM";
          const Icon = telegram ? Send : Globe;
          return (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium">
              <span
                className={cn(
                  "grid size-6 place-items-center rounded-lg",
                  telegram
                    ? "bg-tile-sky text-tile-sky-strong"
                    : "bg-tile-violet text-tile-violet-strong",
                )}
              >
                <Icon className="size-3.5" aria-hidden />
              </span>
              {t(`deliveries.channels.${row.original.channel}`)}
            </span>
          );
        },
      },
      {
        id: "status",
        accessorKey: "status",
        header: t("deliveries.status"),
        meta: {
          label: t("deliveries.status"),
          width: 140,
          filterVariant: "select",
          filterOptions: DELIVERY_STATUSES.map(status => ({
            value: status,
            label: t(`deliveries.statuses.${status}`),
          })),
        },
        cell: ({ row }) => <DeliveryStatusBadge status={row.original.status} />,
      },
      {
        id: "attempts",
        accessorKey: "attempts",
        header: t("deliveries.attempts"),
        meta: { label: t("deliveries.attempts"), width: 100, align: "end" },
        cell: ({ row }) => <span className="tabular-nums">{row.original.attempts}</span>,
      },
      {
        id: "createdAt",
        accessorKey: "createdAt",
        header: t("deliveries.created"),
        meta: { label: t("deliveries.created"), width: 200 },
        cell: ({ row }) => (
          <span className="text-muted-foreground text-xs whitespace-nowrap">
            {formatDateTime(row.original.createdAt, lang)}
          </span>
        ),
      },
      {
        id: "sentAt",
        accessorKey: "sentAt",
        header: t("deliveries.sentAt"),
        meta: { label: t("deliveries.sentAt"), width: 200 },
        cell: ({ row }) => (
          <span className="text-muted-foreground text-xs whitespace-nowrap">
            {formatDateTime(row.original.sentAt, lang)}
          </span>
        ),
      },
      {
        id: "actions",
        header: () => <span className="sr-only">{t("deliveries.retry")}</span>,
        enableHiding: false,
        meta: { width: 170, align: "end" },
        cell: ({ row }) =>
          row.original.status === "FAILED" ? (
            <Button
              variant="outline"
              size="sm"
              className="rounded-xl"
              loading={retryingId === row.original.id}
              disabled={retryingId !== undefined}
              onClick={() => onRetry(row.original.id)}
            >
              <RotateCw />
              {t("deliveries.retry")}
            </Button>
          ) : null,
      },
    ],
    [t, lang, onRetry, retryingId],
  );
}
